import type { ConversationData } from "@/features/chat/Types/conversations.ts";
import type { MessageData } from "@/features/chat/Types/messages.ts";

import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS, WORKSPACES_API_ENDPOINTS } from "@/constants/api.ts";

/**
 * Get conversation details
 *
 * Endpoint: GET /api/conversations/{conversation}
 */
const getConversationData = async (conversationId: string) => {
  return await axiosApiInstance.get<{ data: ConversationData }>(
    `${API_ENDPOINTS.CHAT.CONVERSATION_LIST}/${conversationId}`,
    {},
  );
};

/**
 * Get conversation messages
 *
 * Endpoint: GET /api/conversations/private/messages/{conversation}
 */
const getConversationMessages = async (conversationId: string, offset?: number, limit?: number) => {
  return await axiosApiInstance.get<MessageData>(
    `${API_ENDPOINTS.CHAT.CONVERSATION_LIST}/private/messages/${conversationId}`,
    {
      params: {
        offset: offset ?? 0,
        limit: limit ?? 20,
      },
    },
  );
};

const getLessonConversationMessages = async (
  workspaceId: string,
  lessonId: string,
  offset?: number,
  limit?: number,
) => {
  return await axiosApiInstance.get<MessageData>(
    `${WORKSPACES_API_ENDPOINTS(workspaceId).LESSON_CONVERSATION}/${lessonId}/messages`,
    {
      params: {
        offset: offset ?? 0,
        limit: limit ?? 20,
      },
    },
  );
};

/**
 * Send a message to a conversation
 *
 * Endpoint: POST /api/conversations/private/messages/{conversation}/create
 *
 * @param conversationId - UUID of the conversation
 * @param content - Message content (1-5000 characters)
 * @param attachments - Array of files to attach (max 5)
 */
const sendMessageToApi = async (conversationId: string, content: string, attachments?: File[]) => {
  // Create FormData object
  const formData = new FormData();

  formData.append("content", content);

  // Add attachments if present
  if (attachments && attachments.length > 0) {
    attachments.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });
  }

  return await axiosApiInstance.post(
    `${API_ENDPOINTS.CHAT.CONVERSATION_LIST}/private/messages/${conversationId}/create`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

const sendMessageToLessonChat = async (
  workspaceId: string,
  lessonId: string,
  content: string,
  attachments?: File[],
) => {
  // Create FormData object
  const formData = new FormData();

  formData.append("content", content);

  // Add attachments if present
  if (attachments && attachments.length > 0) {
    attachments.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });
  }

  return await axiosApiInstance.post(
    `${WORKSPACES_API_ENDPOINTS(workspaceId).LESSON_CONVERSATION}/${lessonId}/messages`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

/**
 * Mark conversation as read
 *
 * Endpoint: POST /api/conversations/{conversation}/mark-as-read
 */
const markConversationAsRead = async (conversationId: string) => {
  return await axiosApiInstance.post(
    `${API_ENDPOINTS.CHAT.CONVERSATION_LIST}/${conversationId}/mark-as-read`,
    {},
  );
};

/**
 * Update a message
 *
 * Endpoint: PUT /api/conversations/private/messages/{conversation}/{message}/update
 */
const updateMessageInApi = async (conversationId: string, messageId: string, content: string) => {
  return await axiosApiInstance.put(
    `${API_ENDPOINTS.CHAT.CONVERSATION_LIST}/private/messages/${conversationId}/${messageId}/update`,
    { content },
  );
};

const updateMessageInLessonChat = async (
  workspaceId: string,
  lessonId: string,
  messageId: string,
  content: string,
) => {
  return await axiosApiInstance.put(
    `${WORKSPACES_API_ENDPOINTS(workspaceId).LESSON_CONVERSATION}/${lessonId}/messages/${messageId}`,
    { content },
  );
};

/**
 * Delete a message
 *
 * Endpoint: DELETE /api/conversations/private/messages/{conversation}/{message}/delete
 */
const deleteMessageFromApi = async (conversationId: string, messageId: string) => {
  return await axiosApiInstance.delete(
    `${API_ENDPOINTS.CHAT.CONVERSATION_LIST}/private/messages/${conversationId}/${messageId}/delete`,
  );
};
const deleteMessageFromLessonChat = async (
  workspaceId: string,
  lessonId: string,
  messageId: string,
) => {
  return await axiosApiInstance.delete(
    `${WORKSPACES_API_ENDPOINTS(workspaceId).LESSON_CONVERSATION}/${lessonId}/messages/${messageId}`,
  );
};

export {
  getConversationData,
  getConversationMessages,
  sendMessageToApi,
  markConversationAsRead,
  updateMessageInApi,
  deleteMessageFromApi,
  deleteMessageFromLessonChat,
  getLessonConversationMessages,
  sendMessageToLessonChat,
  updateMessageInLessonChat,
};
